import { MAIN_CONTENT_ID } from 'src/components/SkipToContent/SkipToContent'

/** Suffix Redwood adds via titleTemplate="%PageTitle | %AppTitle". */
export const GOVWELL_APP_TITLE_SUFFIX = ' | GovWell'

/** Strips the app suffix from a browser tab title for screen-reader announcements. */
export const stripGovWellAppTitleSuffix = (title: string): string => {
  if (title.endsWith(GOVWELL_APP_TITLE_SUFFIX)) {
    return title.slice(0, -GOVWELL_APP_TITLE_SUFFIX.length).trim()
  }
  return title.trim()
}

/** Returns trimmed h1 text inside the main content landmark, if present. */
export const getPageHeadingText = (mainContent: HTMLElement | null): string | null => {
  const heading = mainContent?.querySelector('h1')
  const headingText = heading?.textContent?.trim()
  return headingText || null
}

/**
 * Screen readers should hear page content (the heading), not the browser tab
 * title with "| GovWell" separators that VoiceOver reads as "vertical line".
 */
export const resolveRouteAnnouncementText = ({
  mainContent,
  documentTitle,
}: {
  mainContent: HTMLElement | null
  documentTitle: string
}): string => {
  const headingText = getPageHeadingText(mainContent)
  if (headingText) {
    return headingText
  }

  return stripGovWellAppTitleSuffix(documentTitle)
}

export type RouteAnnouncementReadinessInput = {
  previousDocumentTitle: string
  previousHeadingText: string | null
  documentTitle: string
  headingText: string | null
  attempt: number
  maxAttempts: number
  headingGraceAttempts: number
}

/**
 * Waits for Helmet and the in-page h1 to reflect the new route. Avoids announcing
 * while the previous page's heading is still mounted or before async data arrives.
 */
export const isRouteAnnouncementReady = ({
  previousDocumentTitle,
  previousHeadingText,
  documentTitle,
  headingText,
  attempt,
  maxAttempts,
  headingGraceAttempts,
}: RouteAnnouncementReadinessInput): boolean => {
  const timedOut = attempt >= maxAttempts
  if (timedOut) {
    return true
  }

  const titleChanged = documentTitle !== previousDocumentTitle
  if (!titleChanged) {
    return false
  }

  if (headingText) {
    if (previousHeadingText && headingText === previousHeadingText) {
      return false
    }
    return true
  }

  return attempt >= headingGraceAttempts
}

/** Focus target for route changes: page heading first, layout shell as fallback. */
export const getRouteAnnouncementFocusTarget = (mainContent: HTMLElement): HTMLElement | null => {
  const heading = mainContent.querySelector('h1')
  if (heading instanceof HTMLElement && heading.textContent?.trim()) {
    return heading
  }

  return mainContent
}

/** Programmatic focus targets need tabIndex -1 without entering the tab order. */
export const ensureProgrammaticFocusTarget = (element: HTMLElement) => {
  if (!element.hasAttribute('tabindex')) {
    element.tabIndex = -1
  }
}

export const getMainContentElement = (): HTMLElement | null => {
  const mainContent = document.getElementById(MAIN_CONTENT_ID)
  return mainContent instanceof HTMLElement ? mainContent : null
}
